import { Fragment } from 'react';
import type { VehicleResult, VehicleSensitivity } from '../../types';
import { VEHICLE_COLORS } from './TCOSummaryTable';

interface Props {
  vehicles: VehicleResult[];
  sensitivity: VehicleSensitivity[];
}

const APR_DELTAS = [-2, -1, 0, 1, 2];
const RESIDUAL_MULTS = [0.8, 0.9, 1.0, 1.1, 1.2];

function fmt$(v: number): string {
  return '$' + Math.round(v).toLocaleString('en-US');
}

function heatBg(t: number): string {
  // t=0 → green (hue 120), t=1 → red (hue 0)
  const hue = Math.round(120 * (1 - t));
  return `hsl(${hue}, 55%, 20%)`;
}

function heatText(t: number): string {
  const hue = Math.round(120 * (1 - t));
  return `hsl(${hue}, 80%, 68%)`;
}

function getPoint(
  grid: { apr_delta_pct: number; residual_multiplier: number; tco_total: number; residual_value: number }[],
  aprDelta: number,
  rm: number,
) {
  return grid.find(
    (p) => p.apr_delta_pct === aprDelta && Math.abs(p.residual_multiplier - rm) < 0.001,
  );
}

export default function SensitivityHeatmap({ vehicles, sensitivity }: Props) {
  if (!vehicles.length || !sensitivity.length) return null;

  return (
    <div className="space-y-6">
      {/* Item 4: Updated explanation text */}
      <p className="text-sm leading-relaxed rounded-lg px-4 py-3" style={{ color: 'var(--white-dim)', background: 'var(--panel2)', border: '1px solid var(--border)' }}>
        Column headers show estimated resale value at your ownership horizon. Row headers show interest rate scenarios
        relative to your entered rate. Green cells are better outcomes. Red cells are worse. The bold-bordered cell is
        your current assumption.
      </p>

      {vehicles.map((vehicle, vi) => {
        const sens = sensitivity.find((s) => s.vehicle_id === vehicle.vehicle_id);
        if (!sens) return null;

        const grid = sens.sensitivity_grid;
        const allTcos = grid.map((p) => p.tco_total);
        const minTco = Math.min(...allTcos);
        const maxTco = Math.max(...allTcos);
        const range = maxTco - minTco;

        // Item 4: Get residual dollar values for each multiplier column
        // Use the base row (apr_delta=0) to get the residual values
        const residualValues: Map<number, number> = new Map();
        for (const rm of RESIDUAL_MULTS) {
          const basePoint = getPoint(grid, 0, rm);
          if (basePoint) {
            residualValues.set(rm, basePoint.residual_value);
          }
        }

        return (
          <div
            key={vehicle.vehicle_id}
            className="rounded-xl p-5" style={{ background: 'var(--panel)', border: '1px solid var(--border)' }}
          >
            {/* Vehicle label */}
            <h3
              className="text-sm font-semibold mb-4"
              style={{ color: VEHICLE_COLORS[vi % VEHICLE_COLORS.length] }}
            >
              {vehicle.label}
            </h3>

            <div className="overflow-x-auto">
              {/* CSS grid layout for clean cell sizing and border-radius */}
              <div
                className="inline-grid gap-1 min-w-full"
                style={{
                  gridTemplateColumns: `120px repeat(${RESIDUAL_MULTS.length}, 1fr)`,
                }}
              >
                {/* Header row — column labels (X axis = Resale Value) */}
                <div className="px-2 py-1.5 text-xs font-medium" style={{ color: 'var(--white-dim)' }}>
                  <div>Interest Rate</div>
                  <div style={{ color: 'var(--white-dim)' }}>↓ / Resale →</div>
                </div>
                {RESIDUAL_MULTS.map((rm, ri) => {
                  const dollarVal = residualValues.get(rm);
                  return (
                    <div
                      key={rm}
                      className="px-2 py-1.5 text-center text-xs font-semibold"
                    >
                      {ri === 0 && <span className="text-xs block mb-0.5" style={{ color: 'var(--white-dim)' }}>Pessimistic</span>}
                      {ri === RESIDUAL_MULTS.length - 1 && <span className="text-xs block mb-0.5" style={{ color: 'var(--white-dim)' }}>Optimistic</span>}
                      {ri !== 0 && ri !== RESIDUAL_MULTS.length - 1 && <span className="text-xs block mb-0.5" style={{ color: 'var(--border2)' }}>·</span>}
                      {/* Item 4: Show dollar value as primary header */}
                      <span className="block" style={{ color: 'var(--white)' }}>
                        {dollarVal !== undefined ? fmt$(dollarVal) : `×${rm.toFixed(1)}`}
                      </span>
                      {/* Sub-label: multiplier in smaller text */}
                      <span className="text-xs block" style={{ color: 'var(--white-dim)' }}>({rm.toFixed(1)}x)</span>
                    </div>
                  );
                })}

                {/* Data rows */}
                {APR_DELTAS.map((delta, di) => (
                  <Fragment key={delta}>
                    {/* Row label (Y axis = Interest Rate Scenario) */}
                    <div
                      className="px-2 py-2 text-xs font-medium flex flex-col justify-center" style={{ color: 'var(--white-dim)' }}
                    >
                      <span>
                        {delta === 0 ? '±0% APR' : delta > 0 ? `+${delta}% APR` : `${delta}% APR`}
                      </span>
                      {di === 0 && <span className="text-xs" style={{ color: 'var(--white-dim)' }}>Lower Rate</span>}
                      {di === APR_DELTAS.length - 1 && <span className="text-xs" style={{ color: 'var(--white-dim)' }}>Higher Rate</span>}
                    </div>

                    {/* Cells */}
                    {RESIDUAL_MULTS.map((rm) => {
                      const point = getPoint(grid, delta, rm);
                      const isBase = delta === 0 && Math.abs(rm - 1.0) < 0.001;
                      const tco = point?.tco_total ?? 0;
                      const t = range > 1 ? (tco - minTco) / range : 0;

                      return (
                        <div
                          key={`${delta}-${rm}`}
                          className="px-2 py-2 text-center text-xs font-mono rounded"
                          style={{
                            backgroundColor: point ? heatBg(t) : 'transparent',
                            color: point ? heatText(t) : '#475569',
                            border: isBase ? '2px solid #dde3ec' : '2px solid transparent',
                            fontWeight: isBase ? 700 : 400,
                          }}
                        >
                          {point ? fmt$(tco) : '—'}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            </div>

            <p className="text-xs mt-3" style={{ color: 'var(--white-dim)' }}>
              Rows = Interest Rate Scenario (Lower Rate at top, Higher Rate at bottom). Columns = Resale Value Assumption (Pessimistic left, Optimistic right).
            </p>
          </div>
        );
      })}
    </div>
  );
}
